import {
  countPendingAndFailed,
  listJobsBySync,
  listPendingConfirmationsLocal,
  type LocalJob,
} from '../db/jobsDb.js';
import { notifStaleProvisional } from '../../../src/domain/notifications.js';
import { splitProvisionalByAge } from '../../../src/domain/provisional.js';
import { fetchNotifications } from '../api/client.js';
import { listComplianceLocal } from '../db/complianceDb.js';
import {
  isComplianceDue,
  todayISO as complianceToday,
  daysRemaining,
} from '../lib/complianceRoll.js';
import {
  badgeCount,
  markMenuOpened,
} from '../notifications/seen.js';
import { notifSync } from '../notifications/clientItems.js';
import {
  sortNotifications,
  type NotificationItem,
} from '../notifications/merge.js';
import {
  formatLastUpdated,
  getSyncFreshness,
  hasMergeStale,
  isReferenceStale,
} from '../sync/freshness.js';
import { UNSYNCED_WARN_MS } from './status.js';

export type HeaderHooks = {
  onOpenSettings: () => void;
  onSync: () => void;
  onOpenMoneyOverdue: () => void;
  onOpenRenewal: (id: string) => void;
  onOpenSyncSettings: () => void;
  onOpenCalendarUnconfirmed: () => void;
  onOpenWeekSignoff: (weekStart: string) => void;
};

function ageMs(job: LocalJob, now: number): number {
  const t = Date.parse(job.createdAt);
  return Number.isFinite(t) ? now - t : 0;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

async function localRenewalItems(): Promise<NotificationItem[]> {
  const today = complianceToday();
  const compliance = await listComplianceLocal();
  const due = compliance.filter((c) =>
    isComplianceDue({
      expiryDate: c.expiry_date,
      leadTimeDays: c.lead_time_days,
      todayISO: today,
      active: c.active,
      deleted: !!c.deleted_at,
    }),
  );
  return due.map((c) => {
    const rem =
      c.expiry_date != null ? daysRemaining(c.expiry_date, today) : null;
    const remLabel =
      rem == null
        ? ''
        : rem < 0
          ? `${Math.abs(rem)} days overdue`
          : `${rem} days left`;
    const stateToken =
      c.expiry_date && c.expiry_date < today ? 'expired' : 'in_lead';
    return {
      id: `renewal:${c.id}`,
      kind: 'renewal' as const,
      stateToken,
      priority: 20,
      title: c.title,
      subtitle: `${c.issuer ? `${c.issuer} · ` : ''}${c.expiry_date ?? ''}${
        remLabel ? ` · ${remLabel}` : ''
      }`,
      target: { type: 'renewal' as const, id: c.id },
    };
  });
}

/**
 * Top bar: mark + sync chip + notification bell + settings.
 * Structural layout only — polish later.
 */
export async function mountHeader(
  root: HTMLElement,
  hooks: HeaderHooks,
): Promise<{ refresh: () => Promise<void> }> {
  let menuOpen = false;
  let lastItems: NotificationItem[] = [];

  root.innerHTML = `
    <header class="top-bar">
      <img
        class="top-bar-mark"
        src="/icons/icon-192.png"
        alt=""
        width="32"
        height="32"
        decoding="async"
      />
      <button type="button" class="sync-chip" id="header-sync" hidden title="Sync pending">
        Sync
      </button>
      <span class="top-bar-spacer"></span>
      <button type="button" class="icon-btn notif-bell" id="header-notif" aria-label="Notifications" aria-expanded="false" aria-haspopup="true">
        <span aria-hidden="true">🔔</span>
        <span class="notif-badge" id="header-notif-badge" hidden>0</span>
      </button>
      <button type="button" class="icon-btn" id="header-settings" aria-label="Settings">
        ⚙
      </button>
    </header>
    <div id="header-freshness" class="stale-banner" hidden role="status"></div>
    <div class="notif-panel" id="notif-panel" hidden>
      <div class="notif-panel-head">
        <strong>Needs attention</strong>
        <button type="button" class="icon-btn" id="notif-close" aria-label="Close">✕</button>
      </div>
      <div class="notif-panel-body" id="notif-panel-body"></div>
    </div>
  `;

  const syncChip = root.querySelector('#header-sync') as HTMLButtonElement;
  const freshnessEl = root.querySelector('#header-freshness') as HTMLElement;
  const settingsBtn = root.querySelector('#header-settings') as HTMLButtonElement;
  const notifBtn = root.querySelector('#header-notif') as HTMLButtonElement;
  const badgeEl = root.querySelector('#header-notif-badge') as HTMLElement;
  const panel = root.querySelector('#notif-panel') as HTMLElement;
  const panelBody = root.querySelector('#notif-panel-body') as HTMLElement;
  const closeBtn = root.querySelector('#notif-close') as HTMLButtonElement;

  settingsBtn.addEventListener('click', () => {
    menuOpen = false;
    panel.hidden = true;
    notifBtn.setAttribute('aria-expanded', 'false');
    hooks.onOpenSettings();
  });
  syncChip.addEventListener('click', () => hooks.onSync());
  closeBtn.addEventListener('click', () => {
    menuOpen = false;
    panel.hidden = true;
    notifBtn.setAttribute('aria-expanded', 'false');
  });

  function renderPanelBody(items: NotificationItem[]) {
    if (items.length === 0) {
      panelBody.innerHTML = `<p class="muted notif-empty">Nothing needs attention.</p>`;
      return;
    }
    const groups = new Map<string, NotificationItem[]>();
    for (const i of items) {
      const list = groups.get(i.kind) ?? [];
      list.push(i);
      groups.set(i.kind, list);
    }
    const labels: Record<string, string> = {
      stale_provisional: 'Unconfirmed jobs',
      week_release: 'Week sign-off',
      overdue_invoices: 'Overdue invoices',
      renewal: 'Compliance',
      sync: 'Sync',
      scheduled_job: 'Scheduled jobs',
    };
    panelBody.innerHTML = [...groups.entries()]
      .map(
        ([kind, list]) => `<section class="notif-group">
          <h2>${escapeHtml(labels[kind] ?? kind)}</h2>
          <ul>
            ${list
              .map(
                (n) => `<li>
                  <button type="button" class="notif-row" data-notif-id="${escapeHtml(n.id)}">
                    <strong>${escapeHtml(n.title)}</strong>
                    <span class="muted">${escapeHtml(n.subtitle)}</span>
                  </button>
                </li>`,
              )
              .join('')}
          </ul>
        </section>`,
      )
      .join('');

    panelBody.querySelectorAll('[data-notif-id]').forEach((el) => {
      el.addEventListener('click', () => {
        const id = (el as HTMLElement).dataset.notifId!;
        const n = lastItems.find((x) => x.id === id);
        if (!n) return;
        menuOpen = false;
        panel.hidden = true;
        notifBtn.setAttribute('aria-expanded', 'false');
        const t = n.target;
        if (t.type === 'calendar_unconfirmed') hooks.onOpenCalendarUnconfirmed();
        else if (t.type === 'week_signoff') hooks.onOpenWeekSignoff(t.weekStart);
        else if (t.type === 'money_overdue') hooks.onOpenMoneyOverdue();
        else if (t.type === 'renewal') hooks.onOpenRenewal(t.id);
        else if (t.type === 'settings_sync') hooks.onOpenSyncSettings();
        else if (t.type === 'scheduled_job') hooks.onOpenSyncSettings();
      });
    });
  }

  async function localStaleProvisionalItems(): Promise<NotificationItem[]> {
    const today = complianceToday();
    const pending = await listPendingConfirmationsLocal(today);
    const { stale } = splitProvisionalByAge(pending, today);
    return notifStaleProvisional(
      stale.map((j) => ({ id: j.id, work_date: j.work_date })),
    );
  }

  async function collectItems(): Promise<NotificationItem[]> {
    const syncCounts = await countPendingAndFailed();
    let remote: NotificationItem[];
    try {
      const payload = await fetchNotifications();
      remote = payload.items;
    } catch (err) {
      console.warn('header notifications fetch failed; using local cache', err);
      remote = [
        ...(await localRenewalItems()),
        ...(await localStaleProvisionalItems()),
      ];
    }
    if (!remote.some((i) => i.kind === 'stale_provisional')) {
      remote = [...remote, ...(await localStaleProvisionalItems())];
    }
    return sortNotifications([...remote, ...notifSync(syncCounts.pending, syncCounts.failed)]);
  }

  async function openMenu() {
    lastItems = await collectItems();
    renderPanelBody(lastItems);
    await markMenuOpened(lastItems);
    menuOpen = true;
    panel.hidden = false;
    notifBtn.setAttribute('aria-expanded', 'true');
    badgeEl.hidden = true;
  }

  notifBtn.addEventListener('click', () => {
    if (menuOpen) {
      menuOpen = false;
      panel.hidden = true;
      notifBtn.setAttribute('aria-expanded', 'false');
      return;
    }
    void openMenu();
  });

  async function refresh() {
    const { pending, failed } = await countPendingAndFailed();
    const now = Date.now();
    const unsynced = [
      ...(await listJobsBySync('pending')),
      ...(await listJobsBySync('syncing')),
    ];
    const stale = unsynced.some((j) => ageMs(j, now) >= UNSYNCED_WARN_MS);

    if (pending > 0 || failed > 0) {
      syncChip.hidden = false;
      syncChip.className =
        'sync-chip' + (failed > 0 || stale ? ' danger' : ' warn');
      syncChip.textContent =
        failed > 0 ? `${failed} failed` : `${pending} pending`;
    } else {
      syncChip.hidden = true;
    }

    const freshness = await getSyncFreshness();
    const parts: string[] = [];
    if (isReferenceStale(freshness)) {
      parts.push(
        freshness.referenceError
          ? `Lists may be stale: ${freshness.referenceError}`
          : 'Client / site lists not refreshed yet',
      );
      if (freshness.referenceAt) {
        parts.push(`last ok ${formatLastUpdated(freshness.referenceAt)}`);
      }
    }
    if (hasMergeStale(freshness)) {
      const bits: string[] = [];
      if (freshness.jobsError) {
        bits.push(
          `jobs (last ${formatLastUpdated(freshness.jobsAt)})`,
        );
      }
      if (freshness.receiptsError) {
        bits.push(
          `receipts (last ${formatLastUpdated(freshness.receiptsAt)})`,
        );
      }
      if (freshness.complianceError) {
        bits.push(
          `compliance (last ${formatLastUpdated(freshness.complianceAt)})`,
        );
      }
      if (freshness.expensesError) {
        bits.push(
          `expenses (last ${formatLastUpdated(freshness.expensesAt)})`,
        );
      }
      if (bits.length) parts.push(`Cached data: ${bits.join(' · ')}`);
    }
    if (parts.length) {
      freshnessEl.hidden = false;
      freshnessEl.textContent = parts.join(' · ');
    } else {
      freshnessEl.hidden = true;
      freshnessEl.textContent = '';
    }

    lastItems = await collectItems();
    const n = await badgeCount(lastItems);
    if (n > 0 && !menuOpen) {
      badgeEl.hidden = false;
      badgeEl.textContent = n > 9 ? '9+' : String(n);
    } else {
      badgeEl.hidden = true;
    }
    if (menuOpen) renderPanelBody(lastItems);
  }

  await refresh();
  return { refresh };
}
